import pageData, { isReactIframeIntegration } from './utils/pageData/pageData';
import config from './config';
import { fetchActions, parseActions } from './utils/old-riot-code/fetch-actions';
import onIframeComunication from './utils/iframeCommunication';
import watchVisible from './utils/old-riot-code/widget-visible';
import api from './utils/callApi';
import 'iframe-resizer';
import * as Sentry from '@sentry/browser';
import 'promise-polyfill/src/polyfill';
import objectFitImages from 'object-fit-images';

import mainFile, { topLevelComponent } from './main';

import { adBlockCheck, trackAdBlock } from './utils/adBlock';
import staticData from './static_data/all';
import loadScript from './utils/scriptLoader';

// default export is used by react-static to generate static app
export default args => {
    return topLevelComponent(parseActions(staticData.actions));
};

function fetchActionData(page) {
    let prom;
    if (page.widgetRequestId === 'SIDELOAD') {
        prom = Promise.resolve(page).then(parseActions);
    } else if (staticData.useStatic) {
        prom = Promise.resolve(staticData.actions).then(parseActions);
    } else {
        prom = fetchActions(
            config.API_BASE_URL,
            page.articleUrl,
            page.articleUrlTracking,
            page.widgetPreviewId,
            page.widgetRequestId,
            page.embedType,
            page.isSponsoredContent === 'true',
            page.allowSameOrigin
        );
    }

    return prom.then(response => {
        if (response && response.actions && response.actions.length > 0) {
            if (response.isSponsored) {
                return adBlockCheck.then(isAdBlocked => {
                    if (isAdBlocked) {
                        trackAdBlock(response, page);
                        throw new Error('Cannot load sponsored widget due to ad block.');
                    }
                    return response;
                });
            }
            return response;
        }

        throw new Error('No Action Button for this article.');
    });
}

const fetchSavedState = id => {
    return api
        .post('WidgetFormState/Load', { uniqueId: id })
        .catch(err => console.error('Error loading saved state', err));
};

function initWidget(page) {
    var getSavedState = page.uniqueStateId
        ? fetchSavedState(page.uniqueStateId)
        : Promise.resolve();

    return Promise.all([fetchActionData(page), getSavedState])
        .then(async ([data, savedState]) => {
            const isSideloadedData = data.widgetRequestId === 'SIDELOAD';
            if (data && data.cards && data.cards.length > 0) {
                if (!isSideloadedData) {
                    await loadScript(
                        `https://maps.googleapis.com/maps/api/js?key=${
                            config.GOOGLE_MAPS_PUBLIC_KEY
                        }&libraries=places`,
                        null,
                        true,
                        true
                    );
                }
                mainFile(data, page, savedState, isSideloadedData);
                Sentry.init({
                    dsn: 'https://bcdc7526b9c2419d9f7280658f36c835@sentry.io/217945',
                    environment: config.SENTRY_ENV
                });
            }

            if (data.actions.some(action => action.type === "RecordVideo")){
                loadScript('//cdn.addpipe.com/2.0/pipe.js');
                const lnk = document.createElement('link');
                lnk.type='text/css';
                lnk.href='//cdn.addpipe.com/2.0/pipe.css';
                lnk.rel='stylesheet';
                document.getElementsByTagName('head')[0].appendChild(lnk);
            }
        })
        .catch(err =>
            (err || {}).message ? console.log(err.message) : console.error(err)
        );
}

function main() {
    if (isReactIframeIntegration()) {
        let isActionButtonInit = false;

        onIframeComunication(e => {
            if (e.data != null && (e.data.widgetId || e.data.articleUrl)) {
                if (isActionButtonInit) return;
                isActionButtonInit = e.data.widgetRequestId !== 'SIDELOAD';
                const data = Object.assign({}, e.data, {
                    widgetContainerId: config.EMBED_ELEMENT
                });
                initWidget(data).then(() => {
                    window.parent.postMessage(
                        { eventType: 'ActionButtonLoaded', inputData: e.data },
                        '*'
                    );
                });
            }
        });
    } else {
        const page = pageData();

        initWidget(page).then(() => {
            if (typeof window !== 'undefined') {
                watchVisible(null, page.widgetContainer).then(() => {
                    window.postMessage(
                        { isActionButtonWidgetVisible: true },
                        window.origin
                    );
                });
            }
        });
    }
}

function browserSupportsAllFeatures() {
    if (typeof window !== 'undefined') {
        return (
            window.Promise &&
            window.fetch &&
            typeof Object.assign === 'function' &&
            Array.prototype.find
        );
    }
    return false;
}

// if we're in a browser, let's actually run things now
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    if (browserSupportsAllFeatures()) {
        // Browsers that support all features run `main()` immediately.
        main();
    } else {
        // All other browsers loads polyfills and then run `main()`.
        loadScript(
            "https://cdn.polyfill.io/v2/polyfill.min.js?features=Promise,fetch,Object.assign,Array.prototype.find,String.prototype.repeat,Number.isInteger,Object.values",
            main
        );
        objectFitImages()
    }

    document.oncontextmenu = function() {
        return (
            typeof window !== 'undefined' &&
            (window.location.hostname === 'qa-native.actionbutton.co' ||
                window.location.hostname === 'localhost')
        );
    };
}
